import type { Habitat } from './critter-ground.js';

export type QuadKind = 'deer' | 'sheep';

/** Everything that makes one four-legged grazer look and behave differently from another; model and brain are shared. */
export interface QuadSpecies {
  kind: QuadKind;
  habitat: Habitat;
  /** The multiple of real size it is drawn at, so it reads from the isometric camera. */
  scale: number;
  /** Torso radii (x, y, z), leg bone lengths, leg thickness, neck length and head radii, in model metres. */
  torso: [number, number, number]; legUpper: number; legLower: number; legRadius: number; neck: number; head: [number, number, number];
  earLength: number; antlers: boolean; wool: boolean; flag: boolean;
  coat: number; belly: number; legs: number; face: number; hoof: number; inner: number;
  /** Metres at which it notices a threat, walking and fleeing speed (m/s), metres per stride cycle walking, and the range it keeps to about its home. */
  /** A skittish animal freezes and bolts at a gallop, taking its herd with it; a placid one just walks a little way off. */
  skittish: boolean;
  scareReach: number; walkSpeed: number; fleeSpeed: number; stride: number; homeRadius: number;
  /** How many gather in one group, as [fewest, most]. */
  group: [number, number];
}

/** A roe-sized deer: long legs, a slender neck, a white flag, and (the stags) a modest set of antlers. */
export const DEER: QuadSpecies = {
  kind: 'deer', habitat: 'deer', scale: 1.05,
  torso: [.17, .2, .46], legUpper: .34, legLower: .38, legRadius: .028, neck: .42, head: [.075, .085, .15],
  earLength: .15, antlers: true, wool: false, flag: true,
  coat: 0x93663f, belly: 0xd8c7a8, legs: 0x7a5434, face: 0x8a5e3a, hoof: 0x2b2420, inner: 0xe0c8b4,
  skittish: true, scareReach: 15, walkSpeed: .9, fleeSpeed: 7, stride: 1.2, homeRadius: 14, group: [1, 3],
};

/** A hill sheep: a round wool body on short dark legs, a dark face, a short tail. Placid: it walks off a little way from a hero rather than running. */
export const SHEEP: QuadSpecies = {
  kind: 'sheep', habitat: 'sheep', scale: 1.1,
  torso: [.24, .24, .4], legUpper: .17, legLower: .2, legRadius: .026, neck: .2, head: [.07, .085, .13],
  earLength: .1, antlers: false, wool: true, flag: false,
  coat: 0xe6dfcd, belly: 0xd9d1bc, legs: 0x3a322d, face: 0x3d342f, hoof: 0x1d1917, inner: 0x7a5f56,
  skittish: false, scareReach: 5, walkSpeed: .7, fleeSpeed: 5, stride: .85, homeRadius: 9, group: [4, 7],
};

export const QUADS: Record<QuadKind, QuadSpecies> = { deer: DEER, sheep: SHEEP };

/** Height of the torso's centre with the feet on the ground. */
export const standHeight = (species: QuadSpecies) => species.legUpper + species.legLower + species.torso[1] * .45;
